import routes from '@config/routes';
import {
  CHECKOUT_START_DATETIME,
  PACKAGE_STORAGE,
  SELECTED_FLIGHTS_STORAGE,
  SELECTED_HOTEL_STORAGE,
  SELECTED_TICKET_STORAGE,
} from '@config/storageKeys';
import useStorage from '@hooks/useStorage';
import { clearAdminReplaceParams } from '@hooks/use-admin-replace-params';
import { useBuildPackagesStore } from '@store/build-package-store';
import { useCheckoutStepsStore } from '@store/checkout-steps-store';
import { useFlightSearchStore } from '@store/flight-search-store';
import { links } from '@utils/navigation/links';
import {
  cloneShoppingCart,
  getShoppingCartById,
} from '@v2/action/shopping-cart/shopping-cart-action';
import {
  applyQuotedBonusMarkupToCheckoutStore,
  applyQuotedRefundableToCheckoutStore,
} from '@v2/action/shopping-cart/quoted-refundable';
import { CheckoutPayload } from '@v2/action/shopping-cart/types';
import { trackGa4AddToCart } from '@v2/analytics/ga4';
import { setCookie } from 'cookies-next';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState, useTransition } from 'react';
import useSWR from 'swr';

export function useInitBuildPackage() {
  const searchParams = useSearchParams();
  const storage = useStorage();
  const router = useRouter();
  const shoppingCartIdParam = searchParams?.get('shoppingCartId');
  const eventId = searchParams?.get('eventId');

  const [isPending, startTransition] = useTransition();
  const [isRestoring, setIsRestoring] = useState(Boolean(shoppingCartIdParam));
  const restoringCartId = useRef<string | null>(null);

  const { data, isLoading } = useSWR<CheckoutPayload>(
    shoppingCartIdParam
      ? routes.shoppingCartId(shoppingCartIdParam)
      : undefined,
    () => getShoppingCartById(shoppingCartIdParam!),
    { revalidateIfStale: false, revalidateOnFocus: false }
  );

  useEffect(() => {
    if (isLoading) return;
    if (!data || !shoppingCartIdParam) {
      setIsRestoring(false);
      return;
    }
    if (restoringCartId.current === shoppingCartIdParam) return;
    restoringCartId.current = shoppingCartIdParam;

    const restoreCart = async () => {
      // Normal customer package hydration — drop any leftover admin item-replacement
      // session (see clearAdminReplaceParams' own comment for why this leaks).
      clearAdminReplaceParams(storage);

      const workingCart = await cloneShoppingCart(data);
      const values = data.packageConfigDetails;
      const activeCartId = Number(workingCart.id);
      useCheckoutStepsStore.getState().setCartId(activeCartId);
      // pay.ts rejects a charge without the cart's uuid, so a restored cart must carry it too.
      useCheckoutStepsStore.getState().setCartUuid(workingCart.uuid);
      applyQuotedBonusMarkupToCheckoutStore(data);
      applyQuotedRefundableToCheckoutStore(
        { ...data, id: activeCartId },
        activeCartId
      );
      startTransition(() => {
        const isBundle =
          Boolean(data.selectedHotelDetails) &&
          Boolean(data.selectedTicketDetails);

        useBuildPackagesStore.setState({
          ...values,
          isBundle,
        });

        if (data.selectedTicketDetails) {
          trackGa4AddToCart({
            currency: data.packageConfigDetails?.currency || 'USD',
            value:
              (Number(data.selectedTicketDetails.price) || 0) *
              (Number(data.selectedTicketDetails.quantity) || 1),
            item: {
              item_id: `ticket:${data.selectedTicketDetails.ticket_group_id}`,
              item_name: data.packageConfigDetails?.eventName
                ? `${data.packageConfigDetails.eventName} tickets`
                : 'Event tickets',
              item_category: 'tickets',
              price: data.selectedTicketDetails.price,
              quantity: data.selectedTicketDetails.quantity || 1,
              ...(data.selectedTicketDetails.section
                ? { item_variant: data.selectedTicketDetails.section }
                : {}),
            },
            params: {
              is_quoted_cart: true,
              shopping_cart_id: shoppingCartIdParam,
            },
          });
          storage.setItem(
            SELECTED_TICKET_STORAGE,
            JSON.stringify(data.selectedTicketDetails)
          );
        }

        if (data.selectedHotelDetails) {
          trackGa4AddToCart({
            currency: data.packageConfigDetails?.currency || 'USD',
            value: Number(data.selectedHotelDetails.price) || 0,
            item: {
              item_id: `hotel:${data.selectedHotelDetails.id}`,
              item_name: data.selectedHotelDetails.name,
              item_category: 'hotel',
              price: data.selectedHotelDetails.price,
              quantity: data.selectedHotelDetails.rooms || 1,
            },
            params: {
              is_quoted_cart: true,
              shopping_cart_id: shoppingCartIdParam,
            },
          });
          storage.setItem(
            SELECTED_HOTEL_STORAGE,
            JSON.stringify(data.selectedHotelDetails)
          );
        }
        if (data.flightDetails) {
          storage.setItem(
            SELECTED_FLIGHTS_STORAGE,
            JSON.stringify(data.flightDetails.fare)
          );

          useFlightSearchStore.setState({
            ...data.flightDetails.searchParams,
          });
        }
        const selected = {
          tickets: Boolean(data.selectedTicketDetails),
          hotel: Boolean(data.selectedHotelDetails),
          flights: Boolean(data.flightDetails),
        };

        storage.setItem(
          PACKAGE_STORAGE,
          JSON.stringify({ ...values, selected })
        );
        setCookie(CHECKOUT_START_DATETIME, new Date().getTime());
        router.push(`${links.checkout}?eventId=${eventId}`);
      });
    };

    restoreCart().catch(() => {
      restoringCartId.current = null;
      setIsRestoring(false);
    });
  }, [data, eventId, isLoading, router, shoppingCartIdParam, storage]);

  return { isLoading: isLoading || isPending || isRestoring };
}
